import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Mic,
  MicOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FlaskConical,
  FileText,
  RefreshCw,
  WifiOff,
  Settings,
  Bug,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  ExternalLink,
} from "lucide-react";
import {
  checkHealth,
  getExperimentalConfig,
  listTeams,
  mintSession,
  uploadChunk,
  generateDocument,
  createTeam,
  createRecording,
  getDiagnosticsLog,
  subscribeDiagnostics,
  clearDiagnosticsLog,
  getUploadTokenRotationState,
  subscribeUploadTokenRotation,
  resetUploadTokenRotationState,
  getDecodedCurrentIdTokenClaims,
  type Team,
  type DiagnosticsEntry,
  type IdTokenClaims,
  type UploadTokenRotationState,
  type UploadSessionId,
  type RecordingId,
} from "@/lib/experimentalApiClient";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const CHUNK_MAX_BYTES = 25 * 1024 * 1024;
const MAX_CHUNK_RETRIES = 4;
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000];
const MAX_QUEUED_CHUNKS = 50;

function isRetryableChunkError(err: any): boolean {
  if (err?.retryable === true) return true;
  if (err?.stage === "chunk-network") return true;
  const status = err?.status;
  if (typeof status === "number" && status >= 500) return true;
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type RecorderStatus =
  | "idle"
  | "recording"
  | "uploading"
  | "transcribing"
  | "generating"
  | "done"
  | "error";

interface TemplateSection {
  _id: string;
  name: string;
  description: string;
  examples: string[];
  formatTemplate?: string;
}

interface ExperimentalRecorderProps {
  templateSections?: TemplateSection[];
  sessionTitle?: string;
  sessionId?: Id<"recordingSessions">;
  userId?: Id<"users">;
  templateId?: Id<"templates">;
  onDocumentSaved?: () => void;
}

// ---------------------------------------------------------------------------
// Debug panel helpers
// ---------------------------------------------------------------------------

function formatSecondsUntilExpiry(sec: number | null): string {
  if (sec === null) return "unknown";
  if (sec <= 0) {
    const ago = Math.abs(sec);
    if (ago < 60) return `expired ${ago}s ago`;
    if (ago < 3600) return `expired ${Math.floor(ago / 60)}m ago`;
    return `expired ${Math.floor(ago / 3600)}h ago`;
  }
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

function getClaimMismatches(
  claims: IdTokenClaims | null,
  expectedIss: string | null,
  expectedAud: string | null,
): string[] {
  if (!claims) return ["No id token loaded yet — claims not available."];
  const flags: string[] = [];

  if (claims.decodeError) {
    flags.push(`Token decode error: ${claims.decodeError}`);
    return flags;
  }

  if (expectedIss && claims.iss !== expectedIss) {
    flags.push(
      `iss mismatch — token has "${claims.iss ?? "(missing)"}", expected "${expectedIss}"`,
    );
  } else if (!claims.iss) {
    flags.push("iss is missing from token");
  }

  if (expectedAud) {
    const audList = Array.isArray(claims.aud)
      ? claims.aud
      : claims.aud
      ? [claims.aud]
      : [];
    const audMatch = audList.some((a) => a === expectedAud);
    if (!audMatch) {
      flags.push(
        `aud mismatch — token has "${audList.join(", ") || "(missing)"}", expected "${expectedAud}"`,
      );
    }
  } else if (!claims.aud) {
    flags.push("aud is missing from token");
  }

  if (!claims.hasSub) {
    flags.push("sub is missing from token");
  }

  if (claims.secondsUntilExpiry !== null && claims.secondsUntilExpiry <= 0) {
    flags.push(`Token expired ${formatSecondsUntilExpiry(claims.secondsUntilExpiry)}`);
  } else if (claims.secondsUntilExpiry !== null && claims.secondsUntilExpiry < 60) {
    flags.push(`Token expires very soon (${formatSecondsUntilExpiry(claims.secondsUntilExpiry)})`);
  }

  return flags;
}

function buildCopyText(
  log: DiagnosticsEntry[],
  claims: IdTokenClaims | null,
  mismatches: string[],
  rotation: UploadTokenRotationState,
  expectedIss: string | null,
  expectedAud: string | null,
): string {
  const lines: string[] = [
    "=== LRScribe Experimental Diagnostics ===",
    `Generated: ${new Date().toISOString()}`,
    "",
    "--- Id Token Claims ---",
  ];

  if (!claims) {
    lines.push("No id token loaded yet.");
  } else if (claims.decodeError) {
    lines.push(`Decode error: ${claims.decodeError}`);
  } else {
    lines.push(`iss: ${claims.iss ?? "(missing)"}`);
    const audStr = Array.isArray(claims.aud)
      ? claims.aud.join(", ")
      : (claims.aud ?? "(missing)");
    lines.push(`aud: ${audStr}`);
    lines.push(`sub present: ${claims.hasSub ? "yes" : "NO"}`);
    lines.push(`exp: ${claims.expHuman ?? "(missing)"}`);
    lines.push(`expires in: ${formatSecondsUntilExpiry(claims.secondsUntilExpiry)}`);
    lines.push(`expected iss: ${expectedIss ?? "(not configured)"}`);
    lines.push(`expected aud: ${expectedAud ?? "(not configured)"}`);
  }

  lines.push("");
  lines.push("--- Claim Checks ---");
  if (mismatches.length === 0) {
    lines.push("All checks passed.");
  } else {
    mismatches.forEach((m) => lines.push(`WARN: ${m}`));
  }

  lines.push("");
  lines.push("--- Upload Token Rotation ---");
  lines.push(`Rotations: ${rotation.count}`);
  lines.push(`Last rotated: ${rotation.lastRotatedAt ?? "not yet"}`);

  lines.push("");
  lines.push("--- API Call Log ---");
  if (log.length === 0) {
    lines.push("(empty)");
  } else {
    log.forEach((e) => {
      const status = e.httpStatus !== null ? String(e.httpStatus) : "ERR";
      const extra = e.error ? ` [${e.error}]` : "";
      lines.push(`[${e.timestamp}] ${e.method} ${e.path} → ${status} (${e.tokenType})${extra}`);
      if (e.responseBodyText) {
        lines.push(`  body: ${e.responseBodyText.slice(0, 500)}`);
      }
    });
  }

  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Debug panel component
// ---------------------------------------------------------------------------

interface DebugPanelProps {
  oidcConfig: { oidcIssuer: string | null; oidcAudience: string | null } | undefined;
}

function DebugPanel({ oidcConfig }: DebugPanelProps) {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState<DiagnosticsEntry[]>(() => getDiagnosticsLog());
  const [rotation, setRotation] = useState<UploadTokenRotationState>(() =>
    getUploadTokenRotationState(),
  );
  const [claims, setClaims] = useState<IdTokenClaims | null>(() =>
    getDecodedCurrentIdTokenClaims(),
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const unsub = subscribeDiagnostics(() => {
      setLog(getDiagnosticsLog());
      setClaims(getDecodedCurrentIdTokenClaims());
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = subscribeUploadTokenRotation(() => {
      setRotation(getUploadTokenRotationState());
      setClaims(getDecodedCurrentIdTokenClaims());
    });
    return unsub;
  }, []);

  const expectedIss = oidcConfig?.oidcIssuer ?? null;
  const expectedAud = oidcConfig?.oidcAudience ?? null;
  const mismatches = getClaimMismatches(claims, expectedIss, expectedAud);
  const hasMismatches = mismatches.length > 0;

  const handleCopy = () => {
    const text = buildCopyText(log, claims, mismatches, rotation, expectedIss, expectedAud);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="border border-border rounded-lg overflow-hidden text-xs"
      data-testid="experimental-debug-panel"
    >
      <button
        className="w-full flex items-center justify-between px-3 py-2 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid="button-debug-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 font-medium text-muted-foreground">
          <Bug className="w-3.5 h-3.5" />
          <span>Debug</span>
          {hasMismatches && (
            <span className="inline-flex items-center rounded-full bg-destructive/15 text-destructive px-1.5 py-0.5 text-[10px] font-semibold">
              {mismatches.length} issue{mismatches.length !== 1 ? "s" : ""}
            </span>
          )}
          {log.length > 0 && (
            <span className="text-muted-foreground/60 text-[10px]">
              {log.length} call{log.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="p-3 space-y-4 bg-background" data-testid="debug-panel-content">
          {/* Id token claims */}
          <section>
            <h4 className="font-semibold text-foreground mb-2 uppercase tracking-wide text-[10px]">
              Id Token Claims
            </h4>
            {!claims ? (
              <p className="text-muted-foreground italic" data-testid="debug-claims-none">
                No id token loaded yet — make a request first.
              </p>
            ) : claims.decodeError ? (
              <p className="text-destructive" data-testid="debug-claims-error">
                Decode error: {claims.decodeError}
              </p>
            ) : (
              <dl
                className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1"
                data-testid="debug-claims-table"
              >
                <dt className="text-muted-foreground">iss</dt>
                <dd className="font-mono break-all" data-testid="debug-claim-iss">
                  {claims.iss ?? <span className="text-destructive">(missing)</span>}
                </dd>
                <dt className="text-muted-foreground">aud</dt>
                <dd className="font-mono break-all" data-testid="debug-claim-aud">
                  {claims.aud
                    ? Array.isArray(claims.aud)
                      ? claims.aud.join(", ")
                      : claims.aud
                    : <span className="text-destructive">(missing)</span>}
                </dd>
                <dt className="text-muted-foreground">sub</dt>
                <dd data-testid="debug-claim-sub">
                  {claims.hasSub ? (
                    <span className="text-green-600 dark:text-green-400 font-medium">present ✓</span>
                  ) : (
                    <span className="text-destructive font-medium">MISSING ✗</span>
                  )}
                </dd>
                <dt className="text-muted-foreground">exp</dt>
                <dd data-testid="debug-claim-exp">
                  {claims.expHuman ?? <span className="text-destructive">(missing)</span>}
                </dd>
                <dt className="text-muted-foreground">expires in</dt>
                <dd
                  className={
                    claims.secondsUntilExpiry !== null && claims.secondsUntilExpiry <= 0
                      ? "text-destructive font-medium"
                      : claims.secondsUntilExpiry !== null && claims.secondsUntilExpiry < 300
                      ? "text-amber-600 dark:text-amber-400 font-medium"
                      : ""
                  }
                  data-testid="debug-claim-expiry"
                >
                  {formatSecondsUntilExpiry(claims.secondsUntilExpiry)}
                </dd>
              </dl>
            )}
          </section>

          {/* Mismatch flags */}
          <section>
            <h4 className="font-semibold text-foreground mb-2 uppercase tracking-wide text-[10px]">
              Claim Checks
            </h4>
            {hasMismatches ? (
              <ul className="space-y-1" data-testid="debug-mismatch-list">
                {mismatches.map((m, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-1.5 text-destructive"
                    data-testid={`debug-mismatch-${i}`}
                  >
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                className="text-green-600 dark:text-green-400"
                data-testid="debug-mismatch-ok"
              >
                ✓ All checks passed
              </p>
            )}
            {(expectedIss || expectedAud) && (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 mt-2 text-muted-foreground">
                {expectedIss && (
                  <>
                    <dt>expected iss</dt>
                    <dd className="font-mono break-all" data-testid="debug-expected-iss">
                      {expectedIss}
                    </dd>
                  </>
                )}
                {expectedAud && (
                  <>
                    <dt>expected aud</dt>
                    <dd className="font-mono break-all" data-testid="debug-expected-aud">
                      {expectedAud}
                    </dd>
                  </>
                )}
              </dl>
            )}
          </section>

          {/* Upload token rotation */}
          <section>
            <h4 className="font-semibold text-foreground mb-2 uppercase tracking-wide text-[10px]">
              Upload Token Rotation
            </h4>
            <dl
              className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1"
              data-testid="debug-rotation-state"
            >
              <dt className="text-muted-foreground">rotations</dt>
              <dd data-testid="debug-rotation-count">{rotation.count}</dd>
              <dt className="text-muted-foreground">last rotated</dt>
              <dd data-testid="debug-rotation-last">
                {rotation.lastRotatedAt
                  ? new Date(rotation.lastRotatedAt).toLocaleTimeString()
                  : "not yet"}
              </dd>
            </dl>
          </section>

          {/* Call log */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-foreground uppercase tracking-wide text-[10px]">
                API Call Log
              </h4>
              {log.length > 0 && (
                <button
                  className="text-muted-foreground hover:text-foreground underline text-[10px]"
                  onClick={() => {
                    clearDiagnosticsLog();
                    setLog([]);
                  }}
                  data-testid="button-debug-clear-log"
                >
                  Clear
                </button>
              )}
            </div>
            {log.length === 0 ? (
              <p
                className="text-muted-foreground italic"
                data-testid="debug-log-empty"
              >
                No calls logged yet.
              </p>
            ) : (
              <div
                className="max-h-56 overflow-y-auto rounded border border-border bg-muted/30 divide-y divide-border"
                data-testid="debug-log-list"
              >
                {[...log].reverse().map((entry) => (
                  <div
                    key={entry.id}
                    className="px-2 py-1.5 space-y-0.5"
                    data-testid={`debug-log-entry-${entry.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`font-semibold font-mono ${
                          entry.httpStatus === null
                            ? "text-muted-foreground"
                            : entry.httpStatus >= 500
                            ? "text-destructive"
                            : entry.httpStatus >= 400
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                        data-testid="debug-log-status"
                      >
                        {entry.httpStatus !== null ? entry.httpStatus : "ERR"}
                      </span>
                      <span className="font-mono text-muted-foreground">{entry.method}</span>
                      <span className="font-mono break-all text-foreground">{entry.path}</span>
                      <span
                        className={`ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                          entry.tokenType === "id-token"
                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                            : entry.tokenType === "upload-token"
                            ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                            : "bg-muted text-muted-foreground"
                        }`}
                        data-testid="debug-log-token-type"
                      >
                        {entry.tokenType}
                      </span>
                    </div>
                    {entry.error && (
                      <div className="text-amber-600 dark:text-amber-400 font-mono">
                        {entry.error}
                      </div>
                    )}
                    {entry.responseBodyText && (
                      <div
                        className="font-mono text-muted-foreground break-all whitespace-pre-wrap"
                        data-testid="debug-log-body"
                      >
                        {entry.responseBodyText.length > 300
                          ? entry.responseBodyText.slice(0, 300) + "…"
                          : entry.responseBodyText}
                      </div>
                    )}
                    <div className="text-muted-foreground/50 text-[9px]">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Copy diagnostics */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={handleCopy}
              data-testid="button-debug-copy"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copy diagnostics
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ExperimentalRecorder({ templateSections, sessionTitle, sessionId, userId, templateId, onDocumentSaved }: ExperimentalRecorderProps) {
  const { toast } = useToast();
  const updateSession = useMutation(api.recordingSessions.update);

  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [generatedDocument, setGeneratedDocument] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [createTeamName, setCreateTeamName] = useState("");
  const [createTeamLoading, setCreateTeamLoading] = useState(false);
  const [createTeamError, setCreateTeamError] = useState<string | null>(null);
  const [uploadWarning, setUploadWarning] = useState<{ kind: "retrying" | "fatal"; message: string } | null>(null);

  type UnavailableKind =
    | "not-configured"
    | "backend-unreachable"
    | "backend-timeout"
    | "backend-error"
    | "config-error"
    | "auth-error"
    | "unexpected";

  const [unavailableReason, setUnavailableReason] = useState<{
    kind: UnavailableKind;
    detail: string;
  } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const uploadTokenRef = useRef<string>("");
  const sessionIdRef = useRef<UploadSessionId | null>(null);
  const backendRecordingIdRef = useRef<RecordingId | null>(null);
  const sessionExpiresAtRef = useRef<number>(0);
  const teamIdRef = useRef<string>("");
  const chunkQueueRef = useRef<Blob[]>([]);
  const isUploadingChunkRef = useRef(false);
  const chunkErrorRef = useRef<Error | null>(null);
  const uploadFatalRef = useRef<Error | null>(null);
  const chunksSucceededRef = useRef(0);
  const chunksFailedPermanentlyRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const recordingTimeRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const rotationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStoppingRef = useRef(false);
  const rotationInFlightRef = useRef(false);
  const stopDrainResolveRef = useRef<(() => void) | null>(null);
  const negotiatedMimeTypeRef = useRef<string>("");

  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ["/api/experimental-proxy/health", retryKey],
    queryFn: checkHealth,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });

  const { data: oidcConfig, isLoading: oidcLoading } = useQuery({
    queryKey: ["/api/experimental/config", retryKey],
    queryFn: getExperimentalConfig,
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    if (healthLoading || oidcLoading) return;

    if (!healthData?.ok) {
      const reason = (healthData as any)?.reason ?? "unknown";
      let kind: UnavailableKind;
      let detail: string;
      if (reason === "unreachable") {
        kind = "backend-unreachable";
        detail = "Could not reach the transcription backend. Check your connection or try again.";
      } else if (reason === "timeout") {
        kind = "backend-timeout";
        detail = "The backend took too long to respond. It may be temporarily overloaded.";
      } else {
        kind = "backend-error";
        detail = reason && reason !== "unknown"
          ? `Backend returned an error: ${reason}`
          : "The transcription backend returned an unexpected error.";
      }
      setUnavailableReason({ kind, detail });
      setServiceAvailable(false);
      return;
    }

    if (oidcConfig?.fetchError) {
      const { status, message } = oidcConfig.fetchError;
      const kind: UnavailableKind = status === 401 || status === 403 ? "auth-error" : "config-error";
      const detail = status
        ? `Configuration check failed (HTTP ${status}): ${message}`
        : `Configuration check failed: ${message}`;
      setUnavailableReason({ kind, detail });
      setServiceAvailable(false);
      return;
    }

    if (!oidcConfig?.oidcConfigured) {
      setUnavailableReason({
        kind: "not-configured",
        detail: "Authentication for this experimental feature hasn't been set up yet.",
      });
      setServiceAvailable(false);
      return;
    }

    setUnavailableReason(null);
    setServiceAvailable(true);
  }, [healthData, healthLoading, oidcConfig, oidcLoading]);

  const loadTeams = useCallback(async () => {
    setTeamsLoading(true);
    setTeamsError(null);
    try {
      const result = await listTeams();
      setTeams(result);
      if (result.length > 0) {
        const personal = result.find((t) => t.isPersonal) ?? result[0];
        setSelectedTeamId(personal.id);
      }
    } catch (err: any) {
      const httpStatus = err?.status;
      if (httpStatus === 401) {
        const detail = `Authentication failed while loading teams (HTTP 401): ${err?.message ?? "Unauthorized"}`;
        setUnavailableReason({ kind: "auth-error", detail });
        setServiceAvailable(false);
      } else if (httpStatus === 404) {
        setTeamsError(`Team not found (HTTP 404): ${err?.message ?? "The team is not available to you."}`);
      } else if (httpStatus === 502) {
        setTeamsError(`Service error (HTTP 502): ${err?.message ?? "The service is temporarily unavailable."}`);
      } else if (httpStatus) {
        setTeamsError(`Failed to load teams (HTTP ${httpStatus}): ${err?.message ?? "Unknown error"}`);
      } else {
        setTeamsError(err?.message ?? "Failed to load teams — check your connection.");
      }
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (serviceAvailable === true) {
      loadTeams();
    }
  }, [serviceAvailable, loadTeams]);

  const handleCreateTeam = useCallback(async () => {
    const trimmed = createTeamName.trim();
    if (!trimmed) return;
    setCreateTeamLoading(true);
    setCreateTeamError(null);
    try {
      const newTeam = await createTeam(trimmed);
      setTeams([newTeam]);
      setSelectedTeamId(newTeam.id);
      setCreateTeamName("");
    } catch (err: any) {
      const httpStatus = err?.status;
      if (httpStatus === 401) {
        setCreateTeamError(`Authentication failed (HTTP 401): ${err?.message ?? "Unauthorized"}`);
      } else if (httpStatus === 422 || httpStatus === 400) {
        setCreateTeamError(`Invalid team name (HTTP ${httpStatus}): ${err?.message ?? "Please enter a valid name."}`);
      } else if (httpStatus) {
        setCreateTeamError(`Failed to create team (HTTP ${httpStatus}): ${err?.message ?? "Unknown error"}`);
      } else {
        setCreateTeamError(err?.message ?? "Failed to create team — check your connection.");
      }
    } finally {
      setCreateTeamLoading(false);
    }
  }, [createTeamName]);

  const getSupportedMimeType = (): string => {
    const isSafari =
      typeof navigator !== "undefined" &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const candidates = isSafari
      ? ["audio/mp4", "audio/webm", "audio/ogg", "audio/wav"]
      : ["audio/webm", "audio/mp4", "audio/ogg", "audio/wav"];
    for (const mime of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mime)) {
        return mime;
      }
    }
    return "audio/webm";
  };

  const isSessionExpired = (): boolean => {
    const expiresAt = sessionExpiresAtRef.current;
    return expiresAt > 0 && Date.now() >= expiresAt * 1000;
  };

  const uploadChunkFromQueue = useCallback(async () => {
    if (isUploadingChunkRef.current) return;
    isUploadingChunkRef.current = true;

    while (chunkQueueRef.current.length > 0) {
      // Stop draining only on truly unrecoverable errors
      if (uploadFatalRef.current) break;

      if (isSessionExpired()) {
        const err = Object.assign(new Error("Upload session expired"), {
          status: 401,
          errorCode: "expired_token",
          stage: "chunk-session-expired",
        });
        uploadFatalRef.current = err;
        chunkErrorRef.current = err;
        chunkQueueRef.current = [];
        chunksFailedPermanentlyRef.current++;
        setUploadWarning({ kind: "fatal", message: "Upload session expired — recording has stopped uploading." });
        break;
      }

      const raw = chunkQueueRef.current[0];
      if (!raw || raw.size === 0) {
        chunkQueueRef.current.shift();
        continue;
      }

      const chunk = raw;
      if (chunk.size > CHUNK_MAX_BYTES) {
        const err = Object.assign(new Error("Audio chunk exceeds 25 MB limit"), {
          stage: "chunk-too-large",
        });
        uploadFatalRef.current = err;
        chunkErrorRef.current = err;
        chunkQueueRef.current.shift();
        chunksFailedPermanentlyRef.current++;
        setUploadWarning({ kind: "fatal", message: "An audio chunk was too large to upload — upload has stopped." });
        break;
      }

      const uploadSessionId = sessionIdRef.current;
      if (!uploadSessionId) {
        const err = Object.assign(new Error("No upload session available"), {
          stage: "chunk-http",
        });
        uploadFatalRef.current = err;
        chunkErrorRef.current = err;
        chunkQueueRef.current = [];
        chunksFailedPermanentlyRef.current++;
        setUploadWarning({ kind: "fatal", message: "Upload session lost — recording has stopped uploading." });
        break;
      }

      let succeeded = false;
      let lastErr: any = null;
      let hadNetworkError = false;

      for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt++) {
        if (attempt > 0) {
          const delayMs = RETRY_DELAYS_MS[Math.min(attempt - 1, RETRY_DELAYS_MS.length - 1)];
          setUploadWarning({ kind: "retrying", message: `Upload paused — reconnecting… (attempt ${attempt + 1} of ${MAX_CHUNK_RETRIES})` });
          await sleep(delayMs);
          if (uploadFatalRef.current) break;
        }

        try {
          const nextToken = await uploadChunk(
            teamIdRef.current,
            uploadSessionId,
            uploadTokenRef.current,
            chunk,
          );
          uploadTokenRef.current = nextToken;
          chunksSucceededRef.current++;
          succeeded = true;
          setUploadWarning(null);
          break;
        } catch (err: any) {
          lastErr = err;
          if (err?.stage === "chunk-network") {
            hadNetworkError = true;
          }
          if (hadNetworkError && err?.status === 401) {
            lastErr = Object.assign(new Error("Upload token may have been consumed by a timed-out request"), {
              stage: "chunk-token-ambiguous",
              status: 401,
              retryable: false,
            });
            break;
          }
          if (!isRetryableChunkError(err)) break;
        }
      }

      if (succeeded) {
        chunkQueueRef.current.shift();
      } else {
        chunkQueueRef.current.shift();
        chunksFailedPermanentlyRef.current++;
        chunkErrorRef.current = lastErr ?? new Error("Upload failed");

        const isHardAuthErr = lastErr?.status === 401 && lastErr?.stage !== "chunk-token-ambiguous";
        const isTokenAmbiguous = lastErr?.stage === "chunk-token-ambiguous";
        const isTrulyUnrecoverable = isHardAuthErr || isTokenAmbiguous;

        if (isTrulyUnrecoverable) {
          const { description } = describeChunkError(lastErr);
          if (isHardAuthErr) {
            setUnavailableReason({ kind: "auth-error", detail: description });
          }
          uploadFatalRef.current = lastErr;
          chunkQueueRef.current = [];
          setUploadWarning({ kind: "fatal", message: description });
          break;
        }

        // Transient failure exhausted retries: drop this chunk and keep going.
        // Recording continues; the banner stays visible but rotation is not stopped.
        describeChunkError(lastErr ?? new Error("Unknown error"));
        setUploadWarning({
          kind: "fatal",
          message: `Some audio couldn't be uploaded (connection problem) — still recording. Chunks uploaded: ${chunksSucceededRef.current}; failed: ${chunksFailedPermanentlyRef.current}.`,
        });
      }

      if (chunkQueueRef.current.length > MAX_QUEUED_CHUNKS) {
        const err = Object.assign(new Error("Too many chunks queued — memory limit reached"), {
          stage: "chunk-queue-overflow",
        });
        uploadFatalRef.current = err;
        chunkErrorRef.current = err;
        chunkQueueRef.current = [];
        chunksFailedPermanentlyRef.current++;
        setUploadWarning({ kind: "fatal", message: "Upload buffer full — recording has stopped uploading." });
        break;
      }
    }

    isUploadingChunkRef.current = false;
  }, []);

  const describeChunkError = (err: any): { title: string; description: string } => {
    const httpStatus: number | undefined = err?.status;
    const stage: string | undefined = err?.stage;
    const code: string | undefined = err?.errorCode;

    if (stage === "chunk-session-expired" || code === "expired_token") {
      return {
        title: "Upload session expired",
        description: "The recording session timed out. Please start a new recording.",
      };
    }
    if (stage === "chunk-too-large") {
      return {
        title: "Audio chunk too large",
        description: "A chunk exceeded the 25 MB upload limit. Please try a shorter recording.",
      };
    }
    if (stage === "chunk-queue-overflow") {
      return {
        title: "Upload buffer full",
        description: "Too many chunks queued while offline. Some audio could not be uploaded.",
      };
    }
    if (stage === "chunk-token-ambiguous") {
      return {
        title: "Upload interrupted",
        description: "A network interruption may have caused some audio to be lost. Audio already uploaded can still be transcribed.",
      };
    }
    if (stage === "chunk-network") {
      return {
        title: "Network error",
        description: "Couldn't reach the audio service after several attempts. Check your connection.",
      };
    }
    if (httpStatus === 401) {
      return {
        title: "Authentication error",
        description: err?.message
          ? `Upload authentication failed (HTTP 401): ${err.message}`
          : "Upload authentication failed (HTTP 401). Your session may have expired — please start a new recording.",
      };
    }
    if (httpStatus === 400) {
      return {
        title: "Invalid chunk",
        description: "The audio chunk was rejected (empty or wrong format). Please try again.",
      };
    }
    if (httpStatus === 404) {
      return {
        title: "Session not found",
        description: "The upload session was not found. It may have expired — please start a new recording.",
      };
    }
    if (httpStatus === 502) {
      return {
        title: "Service error",
        description: err?.message
          ? `Service error (HTTP 502): ${err.message}`
          : "The audio service returned an error (HTTP 502). Please try again.",
      };
    }
    return {
      title: "Upload failed",
      description: err?.message
        ? `Upload failed${httpStatus ? ` (HTTP ${httpStatus})` : ""}: ${err.message}`
        : "A chunk upload failed. Please try again.",
    };
  };

  const startRecording = async () => {
    if (!selectedTeamId) {
      toast({
        title: "No team selected",
        description: teams.length === 0
          ? "Create a team first before recording."
          : "Please wait for teams to load.",
        variant: "destructive",
      });
      return;
    }

    setStatus("idle");
    setErrorMessage("");
    setGeneratedDocument(null);
    setUploadWarning(null);
    chunkErrorRef.current = null;
    uploadFatalRef.current = null;
    chunkQueueRef.current = [];
    chunksSucceededRef.current = 0;
    chunksFailedPermanentlyRef.current = 0;
    sessionExpiresAtRef.current = 0;
    backendRecordingIdRef.current = null;
    resetUploadTokenRotationState();

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw Object.assign(new Error("Microphone not supported in this browser"), {
          stage: "media",
        });
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();

      setStatusMessage("Starting session…");
      const minted = await mintSession(selectedTeamId);
      sessionIdRef.current = minted.session;
      uploadTokenRef.current = minted.uploadToken;
      teamIdRef.current = selectedTeamId;
      sessionExpiresAtRef.current = minted.expiresAt
        ? Math.floor(new Date(minted.expiresAt).getTime() / 1000)
        : 0;

      const recordingTitle =
        sessionTitle?.trim() || `Recording ${new Date().toLocaleString()}`;
      setStatusMessage("Creating recording…");
      const createdRecording = await createRecording(selectedTeamId, {
        title: recordingTitle,
        description: "",
        notes: "",
      });
      backendRecordingIdRef.current = createdRecording.id;

      const ROTATION_MS = 20_000;
      isStoppingRef.current = false;
      rotationInFlightRef.current = false;
      stopDrainResolveRef.current = null;
      negotiatedMimeTypeRef.current = mimeType;

      const startNextRecorder = () => {
        if (uploadFatalRef.current || isStoppingRef.current) return;

        const activeStream = streamRef.current;
        if (!activeStream) return;

        let rec: MediaRecorder;
        try {
          rec = new MediaRecorder(activeStream, { mimeType: negotiatedMimeTypeRef.current });
        } catch {
          rec = new MediaRecorder(activeStream);
        }
        // Use the actual negotiated mime type the browser chose, not just the candidate
        const actualMimeType = rec.mimeType || negotiatedMimeTypeRef.current || "audio/webm";

        const parts: Blob[] = [];

        rec.ondataavailable = (e) => {
          if (e.data.size > 0) {
            parts.push(e.data);
          }
        };

        rec.onstop = () => {
          rotationInFlightRef.current = false;

          const assembled = new Blob(parts, { type: actualMimeType });
          if (assembled.size > 0) {
            chunkQueueRef.current.push(assembled);
            uploadChunkFromQueue();
          }

          if (isStoppingRef.current) {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            stopDrainResolveRef.current?.();
            stopDrainResolveRef.current = null;
            return;
          }

          startNextRecorder();
        };

        rec.start();
        mediaRecorderRef.current = rec;

        rotationTimerRef.current = setTimeout(() => {
          rotationTimerRef.current = null;
          const current = mediaRecorderRef.current;
          if (current && current.state !== "inactive") {
            rotationInFlightRef.current = true;
            mediaRecorderRef.current = null;
            current.stop();
          }
        }, ROTATION_MS);
      };

      startNextRecorder();

      recordingTimeRef.current = 0;
      setRecordingTime(0);
      setStatus("recording");
      setStatusMessage("Recording…");

      timerRef.current = setInterval(() => {
        recordingTimeRef.current++;
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch (err: any) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;

      const stage = err?.stage ?? "";
      const httpStatus = err?.status;
      let title = "Recording failed";
      let description = "An unexpected error occurred.";

      if (
        httpStatus === 401 ||
        err?.errorCode === "invalid_token" ||
        err?.errorCode === "missing_token"
      ) {
        title = "Authentication error";
        description = httpStatus
          ? `Authentication failed (HTTP ${httpStatus}): ${err?.message ?? "Invalid or missing token"}`
          : `Authentication failed: ${err?.message ?? "Invalid or missing token"}`;
        setUnavailableReason({ kind: "auth-error", detail: description });
        setServiceAvailable(false);
      } else if (stage === "mint-session") {
        if (httpStatus === 404) {
          title = "Team not available";
          description = `This team is not available to you (HTTP 404).${err?.message ? " " + err.message : ""}`;
        } else if (httpStatus === 502) {
          title = "Service error";
          description = err?.message
            ? `Service error starting session (HTTP 502): ${err.message}`
            : "The service returned an error (HTTP 502). Please try again.";
        } else if (httpStatus) {
          title = "Session failed";
          description = err?.message
            ? `Failed to start session (HTTP ${httpStatus}): ${err.message}`
            : `Failed to start session (HTTP ${httpStatus}).`;
        } else {
          title = "Session failed";
          description = err?.message ?? "Couldn't start a recording session.";
        }
      } else if (stage === "create-recording") {
        if (err?.errorCode === "missing_title" || httpStatus === 400) {
          title = "Recording setup failed";
          description = `Could not create the recording: ${err?.message ?? "Invalid request (HTTP 400)."}`;
        } else if (httpStatus === 404) {
          title = "Team not available";
          description = `The team was not found when creating the recording (HTTP 404).${err?.message ? " " + err.message : ""}`;
        } else if (httpStatus === 502) {
          title = "Service error";
          description = err?.message
            ? `Service error creating recording (HTTP 502): ${err.message}`
            : "The service returned an error creating the recording (HTTP 502). Please try again.";
        } else if (httpStatus) {
          title = "Recording setup failed";
          description = err?.message
            ? `Failed to create recording (HTTP ${httpStatus}): ${err.message}`
            : `Failed to create recording (HTTP ${httpStatus}).`;
        } else {
          title = "Recording setup failed";
          description = err?.message ?? "Couldn't create the recording on the server.";
        }
      } else if (stage === "id-token") {
        title = "Sign-in required";
        description = httpStatus
          ? `Could not get an authentication token (HTTP ${httpStatus}): ${err?.message ?? "Please refresh and try again."}`
          : `Could not get an authentication token: ${err?.message ?? "Please refresh and try again."}`;
      } else if (stage === "media" || err?.name === "NotAllowedError") {
        title = "Microphone access denied";
        description = "Please allow microphone access to record audio.";
      } else if (err?.name === "NotFoundError") {
        title = "No microphone found";
        description = "No audio input device was detected.";
      } else {
        description = err?.message ?? description;
      }

      setStatus("error");
      setErrorMessage(description);
      toast({ title, description, variant: "destructive" });
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (rotationTimerRef.current) {
      clearTimeout(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }

    isStoppingRef.current = true;

    const recorder = mediaRecorderRef.current;
    let waitForStop: Promise<void>;

    if (recorder && recorder.state !== "inactive") {
      // Active recorder — stop it and wait for its onstop to fire and enqueue final blob
      waitForStop = new Promise<void>((resolve) => {
        stopDrainResolveRef.current = resolve;
      });
      mediaRecorderRef.current = null;
      recorder.stop();
    } else if (rotationInFlightRef.current) {
      // Rotation timer just fired and called stop(); its onstop is pending
      waitForStop = new Promise<void>((resolve) => {
        stopDrainResolveRef.current = resolve;
      });
    } else {
      // No active recorder and no pending rotation — release stream immediately
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      waitForStop = Promise.resolve();
    }

    setStatus("uploading");
    setStatusMessage("Finishing upload…");

    await waitForStop;

    if (chunkQueueRef.current.length > 0 || isUploadingChunkRef.current) {
      uploadChunkFromQueue();
      await new Promise<void>((resolve) => {
        const check = setInterval(() => {
          if (!isUploadingChunkRef.current && chunkQueueRef.current.length === 0) {
            clearInterval(check);
            resolve();
          }
        }, 200);
      });
    }

    setUploadWarning(null);

    const anyFailed = chunksFailedPermanentlyRef.current > 0;
    const anySucceeded = chunksSucceededRef.current > 0;

    if (anyFailed) {
      const errForDesc: any = uploadFatalRef.current ?? chunkErrorRef.current;
      const { title, description } = errForDesc
        ? describeChunkError(errForDesc)
        : { title: "Some audio not uploaded", description: "Parts of the audio could not be uploaded due to connection problems." };

      const isAuthErr = errForDesc?.status === 401;
      if (isAuthErr) {
        setUnavailableReason({ kind: "auth-error", detail: description });
        setServiceAvailable(false);
      }

      if (anySucceeded) {
        toast({
          title: `${title} — partial recording`,
          description: `${description} Attempting to transcribe the ${chunksSucceededRef.current} chunk${chunksSucceededRef.current !== 1 ? "s" : ""} that did upload.`,
          variant: "destructive",
        });
        await finalizeRecording(true);
        return;
      }

      setStatus("error");
      setErrorMessage(description);
      toast({ title, description, variant: "destructive" });
      return;
    }

    await finalizeRecording(false);
  };

  const finalizeRecording = async (isPartial = false) => {
    setStatus("transcribing");
    setStatusMessage(isPartial ? "Transcribing partial audio… (this may take a moment)" : "Transcribing audio… (this may take a moment)");

    try {
      await new Promise((r) => setTimeout(r, 2000));

      const recordingId = backendRecordingIdRef.current;

      if (!recordingId) {
        const msg =
          "This recording was started before a backend recording ID could be created. " +
          "Please stop and start a new recording to generate a document.";
        setStatus("error");
        setErrorMessage(msg);
        toast({
          title: "Recording must be recreated",
          description: msg,
          variant: "destructive",
        });
        return;
      }

      setStatus("generating");
      setStatusMessage("Generating document…");

      const sections = templateSections ?? [];
      let documentText = "";

      if (sections.length === 0) {
        const result = await generateDocument(teamIdRef.current, recordingId, {
          sectionContents:
            "Generate a structured medical note from the transcribed audio.",
          examples: [],
          formattingInstructions:
            "Use clear Markdown headings and bullet points.",
        });
        documentText = result;
      } else {
        const parts: string[] = [];
        for (const section of sections) {
          const result = await generateDocument(teamIdRef.current, recordingId, {
            sectionContents: section.description || section.name,
            examples: section.examples ?? [],
            formattingInstructions:
              section.formatTemplate ||
              `Generate the "${section.name}" section of the document.`,
          });
          parts.push(result);
        }
        documentText = parts.join("\n\n---\n\n");
      }

      const partialNote = isPartial
        ? `> ⚠️ **Partial recording** — this document was generated from ${chunksSucceededRef.current} uploaded chunk${chunksSucceededRef.current !== 1 ? "s" : ""} only. Some audio was not uploaded due to a connection problem.\n\n`
        : "";
      const finalDocument = partialNote + documentText;

      setGeneratedDocument(finalDocument);
      setStatus("done");
      setStatusMessage(isPartial ? "Document ready (partial recording)" : "Document ready");

      if (sessionId && userId) {
        try {
          await updateSession({
            id: sessionId,
            userId,
            generatedDocument: finalDocument,
            documentGeneratedAt: Date.now(),
            ...(templateId ? { templateId } : {}),
          });
          onDocumentSaved?.();
        } catch (saveErr) {
          console.error("Failed to save experimental document to session:", saveErr);
        }
      }

      toast({
        title: isPartial ? "Document generated (partial recording)" : "Document generated",
        description: isPartial
          ? `Only part of the audio was uploaded. The document reflects ${chunksSucceededRef.current} chunk${chunksSucceededRef.current !== 1 ? "s" : ""} of audio.`
          : "Your recording has been transcribed and documented.",
      });
    } catch (err: any) {
      const httpStatus = err?.status;
      let title = "Generation failed";
      let description = err?.message ?? "Failed to generate the document.";

      if (httpStatus === 401) {
        title = "Authentication error";
        description = `Authentication failed during document generation (HTTP 401): ${err?.message ?? "Unauthorized"}`;
        setUnavailableReason({ kind: "auth-error", detail: description });
        setServiceAvailable(false);
      } else if (httpStatus === 404) {
        title = "Recording not found";
        description =
          `Recording not found (HTTP 404). The session may have expired or is not available to you.`;
      } else if (httpStatus === 409 || httpStatus === 410) {
        title = "Session conflict";
        description =
          "This session is no longer valid. Please start a new recording.";
      } else if (httpStatus === 502) {
        title = "Service error";
        description = err?.message
          ? `Service error during generation (HTTP 502): ${err.message}`
          : "The backend returned an error (HTTP 502). Please try again.";
      } else if (httpStatus) {
        title = "Generation failed";
        description = err?.message
          ? `Generation failed (HTTP ${httpStatus}): ${err.message}`
          : `Generation failed with HTTP ${httpStatus}.`;
      }

      setStatus("error");
      setErrorMessage(description);
      toast({ title, description, variant: "destructive" });
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (rotationTimerRef.current) clearTimeout(rotationTimerRef.current);
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const formatTime = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (healthLoading || oidcLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Checking service availability…</p>
      </div>
    );
  }

  if (serviceAvailable === false) {
    const kind = unavailableReason?.kind ?? "unexpected";
    const detail = unavailableReason?.detail ?? "An unexpected error occurred.";

    const isTransient =
      kind === "backend-unreachable" ||
      kind === "backend-timeout" ||
      kind === "backend-error" ||
      kind === "config-error" ||
      kind === "auth-error" ||
      kind === "unexpected";

    const Icon =
      kind === "backend-unreachable" || kind === "backend-timeout"
        ? WifiOff
        : kind === "not-configured"
        ? Settings
        : AlertCircle;

    const heading =
      kind === "not-configured"
        ? "Not configured yet"
        : kind === "backend-unreachable"
        ? "Backend unreachable"
        : kind === "backend-timeout"
        ? "Backend not responding"
        : kind === "backend-error"
        ? "Backend error"
        : kind === "config-error"
        ? "Configuration error"
        : kind === "auth-error"
        ? "Authentication error"
        : "Unexpected error";

    const iconBg =
      kind === "not-configured"
        ? "bg-muted"
        : "bg-destructive/10";

    const iconColor =
      kind === "not-configured"
        ? "text-muted-foreground"
        : "text-destructive";

    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center px-4"
        data-testid="experimental-unavailable"
        data-unavailable-kind={kind}
      >
        <div className={`w-16 h-16 ${iconBg} rounded-full flex items-center justify-center mb-4`}>
          <Icon className={`w-8 h-8 ${iconColor}`} />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          {heading}
        </h3>
        <p
          className="text-sm text-muted-foreground max-w-sm mb-4"
          data-testid="text-experimental-unavailable-detail"
        >
          {detail}
        </p>
        {isTransient && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setServiceAvailable(null);
              setUnavailableReason(null);
              setRetryKey((k) => k + 1);
            }}
            data-testid="button-experimental-availability-retry"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        )}
        <div className="w-full max-w-sm mt-6">
          <DebugPanel oidcConfig={oidcConfig} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="experimental-recorder">
      <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
        <FlaskConical className="w-3.5 h-3.5 flex-shrink-0" />
        <span>
          Experimental — powered by the new lrscribe backend. Not for production use.
        </span>
      </div>

      {teams.length > 1 && (
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground whitespace-nowrap">
            Team:
          </label>
          <Select
            value={selectedTeamId}
            onValueChange={setSelectedTeamId}
            disabled={status === "recording" || status === "uploading"}
          >
            <SelectTrigger
              className="h-8 text-sm"
              data-testid="select-experimental-team"
            >
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent>
              {teams.map((t) => (
                <SelectItem
                  key={t.id}
                  value={t.id}
                  data-testid={`option-team-${t.id}`}
                >
                  {t.name}
                  {t.isPersonal ? " (personal)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {teamsError && (
        <div
          className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2"
          data-testid="error-experimental-teams"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span data-testid="text-experimental-teams-error">{teamsError}</span>
            <button
              className="ml-2 underline text-destructive/80 hover:text-destructive whitespace-nowrap"
              onClick={loadTeams}
              data-testid="button-experimental-teams-retry"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!teamsLoading && !teamsError && teams.length === 0 && (
        <div
          className="border rounded-lg p-5 space-y-4"
          data-testid="onboarding-create-team"
        >
          <div>
            <h3 className="text-sm font-semibold mb-1">Create your first team</h3>
            <p className="text-sm text-muted-foreground">
              You need a team to start recording. Give it a name and you're ready to go.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Team name"
              value={createTeamName}
              onChange={(e) => setCreateTeamName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !createTeamLoading) handleCreateTeam();
              }}
              disabled={createTeamLoading}
              data-testid="input-create-team-name"
              className="flex-1"
            />
            <Button
              onClick={handleCreateTeam}
              disabled={createTeamLoading || !createTeamName.trim()}
              data-testid="button-create-team"
            >
              {createTeamLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Create"
              )}
            </Button>
          </div>
          {createTeamError && (
            <div
              className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2"
              data-testid="error-create-team"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span data-testid="text-create-team-error">{createTeamError}</span>
            </div>
          )}
        </div>
      )}

      {status === "recording" && uploadWarning && (
        <div
          className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${
            uploadWarning.kind === "retrying"
              ? "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
              : "bg-destructive/10 text-destructive border border-destructive/20"
          }`}
          data-testid="upload-warning-banner"
          data-warning-kind={uploadWarning.kind}
        >
          {uploadWarning.kind === "retrying" ? (
            <Loader2 className="w-4 h-4 mt-0.5 flex-shrink-0 animate-spin" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <span data-testid="text-upload-warning">{uploadWarning.message}</span>
            {uploadWarning.kind === "fatal" && (
              <p className="text-xs mt-0.5 opacity-80">
                Recording is still capturing audio. Press stop to transcribe what was uploaded.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col items-center gap-4 py-6">
        {status === "idle" || status === "error" ? (
          <Button
            size="lg"
            className="w-20 h-20 rounded-full"
            onClick={startRecording}
            disabled={teamsLoading || !selectedTeamId}
            data-testid="button-experimental-start"
          >
            {teamsLoading ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <Mic className="w-8 h-8" />
            )}
          </Button>
        ) : status === "recording" ? (
          <Button
            size="lg"
            variant="destructive"
            className="w-20 h-20 rounded-full"
            onClick={stopRecording}
            data-testid="button-experimental-stop"
          >
            <MicOff className="w-8 h-8" />
          </Button>
        ) : status === "done" ? (
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
        ) : (
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
          </div>
        )}

        <div className="text-center">
          {status === "recording" && (
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span
                className="text-sm font-mono text-foreground"
                data-testid="text-experimental-timer"
              >
                {formatTime(recordingTime)}
              </span>
            </div>
          )}
          <p
            className="text-sm text-muted-foreground"
            data-testid="text-experimental-status"
          >
            {status === "idle" && !teamsLoading && teams.length === 0 && "Create a team above to start recording"}
            {status === "idle" && !teamsLoading && teams.length > 0 && "Press the microphone to start recording"}
            {status === "idle" && teamsLoading && "Loading…"}
            {status === "recording" && "Recording — press to stop"}
            {(status === "uploading" ||
              status === "transcribing" ||
              status === "generating") &&
              statusMessage}
            {status === "done" && "Complete"}
            {status === "error" && errorMessage}
          </p>
          {status === "error" && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                setStatus("idle");
                setErrorMessage("");
              }}
              data-testid="button-experimental-retry"
            >
              Try again
            </Button>
          )}
        </div>
      </div>

      {(status === "uploading" ||
        status === "transcribing" ||
        status === "generating") && (
        <div className="space-y-2">
          {(["uploading", "transcribing", "generating"] as const).map((step) => {
            const stepIdx = ["uploading", "transcribing", "generating"].indexOf(step);
            const curIdx = ["uploading", "transcribing", "generating"].indexOf(status);
            const isDone = stepIdx < curIdx;
            const isActive = step === status;
            return (
              <div
                key={step}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? "bg-primary/10 text-foreground"
                    : isDone
                    ? "text-muted-foreground"
                    : "text-muted-foreground/50"
                }`}
                data-testid={`status-step-${step}`}
              >
                {isActive ? (
                  <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                ) : isDone ? (
                  <span className="w-4 h-4 flex-shrink-0 text-green-500">✓</span>
                ) : (
                  <span className="w-4 h-4 flex-shrink-0 text-muted-foreground/30">○</span>
                )}
                <span className="capitalize">
                  {step === "uploading"
                    ? "Uploading audio"
                    : step === "transcribing"
                    ? "Transcribing"
                    : "Generating document"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {generatedDocument && (
        <div
          className="border rounded-lg overflow-hidden"
          data-testid="experimental-document"
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-muted/40 border-b">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Generated Document</span>
          </div>
          <div className="p-4 prose prose-sm max-w-none dark:prose-invert text-sm leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {generatedDocument}
            </ReactMarkdown>
          </div>
          <div className="px-4 py-2 bg-muted/40 border-t flex items-center gap-2">
            {onDocumentSaved && (
              <Button
                variant="default"
                size="sm"
                onClick={onDocumentSaved}
                data-testid="button-experimental-view-document"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                View in Document tab
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStatus("idle");
                setGeneratedDocument(null);
                setRecordingTime(0);
              }}
              data-testid="button-experimental-new"
            >
              New recording
            </Button>
          </div>
        </div>
      )}

      <DebugPanel oidcConfig={oidcConfig} />
    </div>
  );
}
